import React, { useMemo, useState } from 'react';
import { Table, Checkbox, Typography } from '@coolremote/shared';
import { t } from 'ttag';
import { orderBy } from 'lodash';
import useStyles from '../styles';

/**
 * @typedef {Object} DiscoveredDevice
 * @property {string} id - Device unique identifier
 * @property {string} name - Device name
 * @property {string} [vendorName] - Vendor display name
 * @property {number|string} [vendorId] - Vendor ID
 * @property {string} [modelName] - Device model name
 * @property {string} [macAddress] - MAC Address or IP:Port
 * @property {string} [device] - Parent CoolAutomation device ID
 * @property {string|number} [line] - BACnet line index
 */

/**
 * Results component displaying discovered BACnet devices table.
 *
 * @param {Object} props
 * @param {DiscoveredDevice[]} props.devices - List of discovered BACnet devices
 * @param {Set<string>} props.selectedDeviceIds - Set of checked device IDs
 * @param {(deviceId: string) => void} props.onToggleSelect - Handler for toggling single device selection
 * @param {(allVisibleIds: string[]) => void} props.onToggleSelectAll - Handler for toggling select all visible devices
 * @param {Record<string, any>} [props.allDevices] - Dictionary of all CoolAutomation devices for serial resolution
 * @param {boolean} props.isSaving - Whether the save action is currently in progress
 * @returns {React.ReactElement}
 */
const Results = ({
  devices,
  selectedDeviceIds,
  onToggleSelect,
  onToggleSelectAll,
  allDevices,
  isSaving,
}) => {
  const classes = useStyles();

  // Sorting state
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Handle sort request
  const handleRequestSort = (field) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  // Render header sorting helper
  const renderSortableHeader = (label, field) => {
    return (
      <div
        onClick={() => handleRequestSort(field)}
        className={classes.headerCellWrapper}
      >
        <span>{label}</span>
      </div>
    );
  };

  // Sorted devices
  const sortedDevices = useMemo(() => {
    return orderBy(devices, [sortField], [sortDirection]);
  }, [devices, sortField, sortDirection]);

  const allVisibleIds = sortedDevices.map((d) => d.id);
  const isSelectAllChecked = allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedDeviceIds.has(id));
  const isSelectAllIndeterminate = allVisibleIds.length > 0 && !isSelectAllChecked && allVisibleIds.some((id) => selectedDeviceIds.has(id));

  const handleSelectAllChange = () => {
    onToggleSelectAll(allVisibleIds);
  };

  // Table Column Definitions
  const columns = useMemo(() => [
    {
      id: 'checkbox',
      width: 40,
      title: (
        <Checkbox
          indeterminate={isSelectAllIndeterminate}
          checked={isSelectAllChecked}
          onChange={handleSelectAllChange}
          disabled={isSaving}
          className={classes.customCheckbox16}
        />
      ),
      render: (device) => (
        <Checkbox
          checked={selectedDeviceIds.has(device.id)}
          onChange={() => onToggleSelect(device.id)}
          disabled={isSaving}
          className={classes.customCheckbox16}
        />
      ),
    },
    {
      id: 'name',
      title: renderSortableHeader(t`BACNET DEVICE NAME`, 'name'),
      render: (device) => (
        <Typography variant="body2" className={classes.deviceNameBold}>
          {device.name}
        </Typography>
      ),
    },
    {
      id: 'vendorName',
      title: renderSortableHeader(t`VENDOR NAME (ID)`, 'vendorName'),
      render: (device) => (
        <Typography variant="body2">
          {device.vendorName}
          {device.vendorId && ` (${device.vendorId})`}
        </Typography>
      ),
    },
    {
      id: 'modelName',
      title: renderSortableHeader(t`MODEL`, 'modelName'),
      render: (device) => device.modelName || '-',
    },
    {
      id: 'macAddress',
      title: renderSortableHeader(t`MAC ADDR`, 'macAddress'),
      render: (device) => device.macAddress || '-',
    },
    {
      id: 'caDeviceSnLine',
      title: renderSortableHeader(t`CA DEVICE SN (LINE)`, 'device'),
      render: (device) => {
        const caDevice = allDevices?.[device.device];
        const serial = caDevice?.serial || device.device || '-';
        const lineLabel = device.line == '0' ? 'IP' : `Line ${device.line}`;
        return (
          <Typography variant="body2">
            {`${serial} (${lineLabel})`}
          </Typography>
        );
      },
    },
  ], [
    isSelectAllIndeterminate,
    isSelectAllChecked,
    isSaving,
    selectedDeviceIds,
    allVisibleIds,
    allDevices,
    onToggleSelect,
    onToggleSelectAll,
    sortField,
    sortDirection,
  ]);

  return (
    <div className={classes.tableContainer}>
      <Table columns={columns} data={sortedDevices} />
    </div>
  );
};

export default Results;
